import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shop } from '@/api/shop';
import type { ProductRow, CreateProductBody } from '@/api/shop';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Plus, MoreHorizontal, Trash2, Package, Search,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

export function ProductsList() {
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const canMutate = CAN_MUTATE.includes(user.role);
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ProductRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['products', search, effectiveCustomerId],
    queryFn: () => shop.listProducts({
      search: search.trim() || undefined,
      limit: 200,
      customer_id: effectiveCustomerId ?? undefined,
    }),
  });
  const rows = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => shop.removeProduct(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['products'] }); setDeleteTarget(null); },
  });

  return (
    <>
      <PageHeader
        eyebrow="Shop"
        title="Products"
        description="Catalog for voice shopping. The IVR shopping node resolves a product by id; stock counts here are authoritative."
        actions={canMutate && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            New product
          </Button>
        )}
      />

      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
        <Input
          placeholder="Search by name or SKU…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState onCreate={canMutate ? () => setCreateOpen(true) : undefined} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {rows.map((p) => (
            <ProductCard
              key={p.id} row={p}
              canMutate={canMutate}
              onDelete={() => setDeleteTarget(p)}
            />
          ))}
        </div>
      )}

      {canMutate && <CreateProductDialog open={createOpen} onOpenChange={setCreateOpen} />}

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete product?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">{deleteTarget?.name}</span> will be soft-deleted.
            Existing orders with this product keep their line items.
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" size="sm">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive" size="sm"
              disabled={remove.isPending}
              onClick={() => deleteTarget && remove.mutate(deleteTarget.id)}
            >
              {remove.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EmptyState({ onCreate }: { onCreate?: () => void }) {
  return (
    <Card>
      <CardContent className="py-16 text-center">
        <Package className="mx-auto h-8 w-8 text-slate-300 mb-3" />
        <p className="text-sm font-medium text-slate-500">No products yet</p>
        <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
          Add a product, then point an IVR{' '}
          <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">shopping</code>{' '}
          node at its id. Callers reach that node and a cart is created + submitted for checkout.
        </p>
        {onCreate && (
          <Button size="sm" className="mt-4" onClick={onCreate}>
            <Plus className="h-4 w-4 mr-1.5" />
            Create your first product
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function ProductCard({
  row, canMutate, onDelete,
}: {
  row: ProductRow; canMutate: boolean; onDelete: () => void;
}) {
  const stockLabel = row.stock_qty === null
    ? '∞'
    : row.stock_qty === 0 ? 'OUT'
      : String(row.stock_qty);
  const stockLow = row.stock_qty !== null && row.stock_qty > 0 && row.stock_qty <= 5;
  const stockOut = row.stock_qty !== null && row.stock_qty === 0;

  return (
    <article className={cn(
      'group relative rounded-md border-2 bg-white overflow-hidden transition-all',
      row.active ? 'border-slate-900' : 'border-slate-200',
      'hover:-translate-y-0.5 hover:shadow-lg',
    )}>
      {/* Top ticker */}
      <div className={cn(
        'flex items-center justify-between px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border-b-2',
        row.active ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-100 text-slate-400 border-slate-200',
      )}>
        <span>SKU</span>
        <span className="font-bold">{row.sku ?? '—'}</span>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3 className="text-base font-semibold text-foreground leading-tight truncate flex-1">
            {row.name}
          </h3>
          {canMutate && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  size="icon" variant="ghost"
                  className="h-6 w-6 -mt-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={(e) => { e.preventDefault(); onDelete(); }}
                  className="text-destructive focus:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {row.description && (
          <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
            {row.description}
          </p>
        )}

        <div className="flex items-end justify-between pt-3 border-t border-dashed border-slate-300">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Price</div>
            <div className="font-mono tabular-nums text-2xl font-bold leading-none mt-1">
              {row.currency}{' '}
              <span className="text-slate-900">
                {(row.price_cents / 100).toFixed(2)}
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Stock</div>
            <div className={cn(
              'font-mono tabular-nums text-sm font-bold mt-1 leading-none inline-block px-2 py-1 rounded',
              stockOut ? 'bg-rose-100 text-rose-900 ring-1 ring-rose-200'
                : stockLow ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-200'
                  : row.stock_qty === null ? 'text-slate-400'
                    : 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200',
            )}>
              {stockLabel}
            </div>
          </div>
        </div>

        {!row.active && (
          <div className="mt-3 text-[10px] text-center uppercase tracking-widest font-mono text-slate-400 border-t border-slate-200 pt-2">
            — Inactive —
          </div>
        )}
      </div>
    </article>
  );
}

function CreateProductDialog({
  open, onOpenChange,
}: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [sku, setSku] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [stock, setStock] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const { effectiveCustomerId, element: customerPicker, isPickerVisible } =
    useCustomerPicker(customerId, setCustomerId, { required: true });

  const create = useMutation({
    mutationFn: (body: CreateProductBody) => shop.createProduct(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      setName(''); setSku(''); setDescription(''); setPrice(''); setStock('');
      setCustomerId(null); setError(null);
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      setError(err instanceof Error ? err.message : 'Create failed.'),
  });

  const priceDollars = Number(price);
  const priceCents = Number.isFinite(priceDollars) ? Math.round(priceDollars * 100) : -1;
  const stockInt = stock.trim() === '' ? null : Number(stock);
  const validStock = stock.trim() === '' || (Number.isInteger(stockInt) && (stockInt ?? 0) >= 0);
  const canSubmit = !!name.trim() && priceCents >= 0 && validStock && effectiveCustomerId !== null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New product</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {customerPicker}
          <CustomerScopeChip />
          <div className="grid grid-cols-[1fr_8rem] gap-3">
            <div className="space-y-1">
              <Label htmlFor="prod-name">Name</Label>
              <Input
                id="prod-name" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Course Book Vol 1"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="prod-sku">SKU (optional)</Label>
              <Input
                id="prod-sku" value={sku} onChange={(e) => setSku(e.target.value)}
                placeholder="BOOK-001"
                className="font-mono"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="prod-desc">Description (optional)</Label>
            <Textarea
              id="prod-desc" rows={2}
              value={description} onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="prod-price">Price (USD)</Label>
              <Input
                id="prod-price" type="number" min="0" step="0.01"
                value={price} onChange={(e) => setPrice(e.target.value)}
                className="font-mono"
                placeholder="19.99"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="prod-stock">Stock (optional)</Label>
              <Input
                id="prod-stock" type="number" min="0" step="1"
                value={stock} onChange={(e) => setStock(e.target.value)}
                className="font-mono"
                placeholder="unlimited"
              />
            </div>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={create.isPending}>Cancel</Button>
          </DialogClose>
          <Button
            disabled={create.isPending || !canSubmit}
            onClick={() => create.mutate({
              name: name.trim(),
              sku: sku.trim() || null,
              description: description.trim() || null,
              ...(effectiveCustomerId
                ? { customer_id: effectiveCustomerId }
                : {}),
              price_cents: priceCents,
              stock_qty: stockInt ?? null,
            })}
          >
            {create.isPending ? 'Creating…' : 'Create product'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
